/**
 * Arquivo: src/context/PeriodContext.tsx
 * Versão: 4.8.0
 * Data: 08/09/2026
 */
import { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react'

export type PeriodoSelecionado = 'ALL' | string

type PeriodContextValue = {
  periodo: PeriodoSelecionado
  setPeriodo: (valor: PeriodoSelecionado) => void
  inicio?: string
  fim?: string
  label: string
  dentroDoPeriodo: (data?: string | null) => boolean
}

const PeriodContext = createContext<PeriodContextValue | null>(null)

const nomeMes = (valor: string) => {
  if (valor === 'ALL') return 'Todos os meses'
  const [ano, mes] = valor.split('-').map(Number)
  if (!ano || !mes) return valor
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' })
    .format(new Date(ano, mes - 1, 1))
    .replace(/^./, c => c.toUpperCase())
}

export function PeriodProvider({ children }: PropsWithChildren) {
  const agora = new Date()
  const padrao = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`
  const [periodo, setPeriodoState] = useState<PeriodoSelecionado>(() => localStorage.getItem('cf_periodo') || padrao)

  const setPeriodo = (valor: PeriodoSelecionado) => {
    setPeriodoState(valor)
    localStorage.setItem('cf_periodo', valor)
  }

  const value = useMemo<PeriodContextValue>(() => {
    if (periodo === 'ALL') {
      return {
        periodo,
        setPeriodo,
        label: 'Todos os meses',
        dentroDoPeriodo: () => true,
      }
    }
    const [ano, mes] = periodo.split('-').map(Number)
    const ultimoDia = new Date(ano, mes, 0).getDate()
    const inicio = `${periodo}-01`
    const fim = `${periodo}-${String(ultimoDia).padStart(2, '0')}`
    return {
      periodo,
      setPeriodo,
      inicio,
      fim,
      label: nomeMes(periodo),
      dentroDoPeriodo: (data?: string | null) => !!data && String(data).slice(0, 7) === periodo,
    }
  }, [periodo])

  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>
}

export function usePeriod() {
  const ctx = useContext(PeriodContext)
  if (!ctx) throw new Error('usePeriod deve ser usado dentro de PeriodProvider.')
  return ctx
}

export function opcoesMeses() {
  const hoje = new Date()
  const lista: { value: string; label: string }[] = [{ value: 'ALL', label: 'Todos os meses' }]
  const offsets = [0, ...Array.from({ length: 48 }, (_, i) => -(i + 1)), ...Array.from({ length: 12 }, (_, i) => i + 1)]
  for (const offset of offsets) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() + offset, 1)
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    lista.push({ value, label: nomeMes(value) })
  }
  return lista
}
